import { useEffect, useState } from 'react';
import { UserService } from './Service';
import { useAppContext } from '../../../contexts/AppContext';
import { PushNotification } from '../../../PushNotification';
import { SettingsRow, SettingsSection } from '../estabelecimento/Form/SettingsGroup';

const service = new UserService();

interface UserSettings {
    emailNotifications: boolean;
    pushNotifications: boolean;
}

const getBrowserNotificationPermission = (): NotificationPermission | null => {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
        return null;
    }

    return Notification.permission;
};

const ToggleRow = ({
    label,
    description,
    name,
    checked,
    onChange,
}: {
    label: string;
    description: string;
    name: string;
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
    <SettingsRow label={label} htmlFor={name} description={description} inline>
        <input
            id={name}
            type="checkbox"
            className="toggle toggle-success"
            name={name}
            checked={checked}
            onChange={onChange}
        />
    </SettingsRow>
);

const NotificationsForm: React.FC = () => {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [settings, setSettings] = useState<UserSettings>({
        emailNotifications: false,
        pushNotifications: false,
    });
    const [loading, setLoading] = useState(false);
    const [activatingBrowserPush, setActivatingBrowserPush] = useState(false);
    const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(getBrowserNotificationPermission);

    useEffect(() => {
        setBrowserPermission(getBrowserNotificationPermission());

        service
            .getSettings()
            .then(({ data }) => {
                if (data) {
                    setSettings({
                        emailNotifications: !!data.emailNotifications,
                        pushNotifications: !!data.pushNotifications,
                    });
                }
            })
            .catch(() => {
                errorSnackbar('Erro ao buscar configurações da conta.');
            });
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        setSettings((prevSettings) => ({
            ...prevSettings,
            [name]: checked,
        }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        setLoading(true);

        service
            .updateSettings(settings)
            .then(() => {
                successSnackbar('Configurações de notificação salvas com sucesso!');
            })
            .catch(() => {
                errorSnackbar('Erro ao salvar configurações.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleActivateBrowserPush = async () => {
        setActivatingBrowserPush(true);

        try {
            const permission = await PushNotification.requestAndSaveToken();
            const nextPermission = permission || getBrowserNotificationPermission();
            setBrowserPermission(nextPermission);

            if (nextPermission === 'granted') {
                successSnackbar('Notificações do navegador ativadas.');
                return;
            }

            if (nextPermission === 'denied') {
                errorSnackbar(
                    'Permissão bloqueada. Acesse as configurações do navegador ou do aplicativo no celular, ative as notificações para este site e tente novamente.'
                );
                return;
            }

            errorSnackbar('Permissão de notificações não concedida.');
        } finally {
            setActivatingBrowserPush(false);
        }
    };

    const notificationSupportAvailable =
        typeof window !== 'undefined' &&
        typeof Notification !== 'undefined' &&
        'serviceWorker' in navigator;
    const showActivateButton =
        notificationSupportAvailable && browserPermission !== 'granted';
    const activateButtonLabel = browserPermission === 'denied'
        ? 'Tentar ativar'
        : 'Ativar no dispositivo';

    const browserPermissionLabel = (() => {
        if (!notificationSupportAvailable) return 'Não suportado neste navegador/ambiente';
        if (browserPermission === 'granted') return 'Ativado';
        if (browserPermission === 'denied') return 'Bloqueado';
        return 'Aguardando permissão';
    })();

    const browserPermissionHelp = (() => {
        if (!notificationSupportAvailable) {
            return 'Este dispositivo/navegador não suporta notificações push web.';
        }

        if (browserPermission === 'granted') {
            return 'Notificações ativas neste dispositivo logado.';
        }

        if (browserPermission === 'denied') {
            return 'Neste dispositivo a permissão já foi negada anteriormente. Para ativar, acesse as configurações do navegador (ou do aplicativo, no celular) e permita notificações para este site.';
        }

        return 'Ao abrir o app, tentamos solicitar permissão automaticamente neste dispositivo no máximo 1 vez por dia.';
    })();

    return (
        <form onSubmit={handleSubmit}>
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <SettingsSection title="Como receber">
                    <ToggleRow
                        label="E-mail"
                        description="Atualizações, confirmações e lembretes por e-mail."
                        name="emailNotifications"
                        checked={settings.emailNotifications}
                        onChange={handleInputChange}
                    />

                    <ToggleRow
                        label="Push"
                        description="Alertas rápidos diretamente no seu dispositivo."
                        name="pushNotifications"
                        checked={settings.pushNotifications}
                        onChange={handleInputChange}
                    />
                </SettingsSection>

                <SettingsSection title="Este dispositivo" footer={browserPermissionHelp}>
                    <SettingsRow label="Status">
                        <span className="estab-row-value">{browserPermissionLabel}</span>
                    </SettingsRow>
                    {showActivateButton && (
                        <SettingsRow inline>
                            <button
                                type="button"
                                className="estab-link-button justify-self-start"
                                onClick={() => void handleActivateBrowserPush()}
                                disabled={activatingBrowserPush}
                            >
                                {activatingBrowserPush && <span className="loading loading-spinner loading-xs"></span>}
                                {activatingBrowserPush ? 'Ativando...' : activateButtonLabel}
                            </button>
                        </SettingsRow>
                    )}
                </SettingsSection>
            </div>

            <div className="estab-savebar mt-6">
                <button
                    className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                    type="submit"
                    disabled={loading}
                >
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    Salvar alterações
                </button>
            </div>
        </form>
    );
};

export default function ConfiguracoesContaNotifications() {
    return <NotificationsForm />;
}
